import { useEffect, useRef } from "react";

// region Constants
// Time on screen that counts as seen. Long enough that scrolling past
// something does not register as reading it.
const EXPOSURE_MS = 2000;

// Most of the element has to be visible; a sliver at the edge is not exposure.
const VISIBLE_RATIO = 0.6;
// endregion Constants

/**
 * Reports that an element was actually on the user's screen for a while.
 *
 * Returns a ref to attach to the element. Fires at most once, and only while
 * the tab is in the foreground: a notification sitting in a background tab is
 * not being read. Leaving the tab mid-countdown restarts it on return.
 */
export const useExposure = <T extends HTMLElement>(enabled: boolean, onExposed: () => void) => {

  const elementRef = useRef<T | null>(null);

  // Callers pass an inline arrow, so the callback is read from a ref: as an
  // effect dependency it would rebuild the observer on every render and restart
  // the countdown forever.
  const onExposedRef = useRef(onExposed);

  onExposedRef.current = onExposed;

  useEffect(() => {
    const element = elementRef.current;

    if (!enabled || !element) return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const cancel = () => {
      if (timer) clearTimeout(timer);

      timer = null;
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio < VISIBLE_RATIO || document.hidden) {
        cancel();

        return;
      }

      if (timer) return;

      timer = setTimeout(() => {
        observer.disconnect();
        onExposedRef.current();
      }, EXPOSURE_MS);
    }, { threshold: VISIBLE_RATIO });

    observer.observe(element);
    document.addEventListener("visibilitychange", cancel);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", cancel);
      cancel();
    };
  }, [enabled]);

  return elementRef;
};
